'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const yalovaProduct: Product = {
  slug: 'yalova-bondex-sunger',
  name: 'Yalova Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Yalova bondex sünger; konut, villa, yazlık, otel ve teknik hacim projelerinde ses ve titreşim kontrolü için yüksek yoğunluklu çözüm sunar.',
  description:
    'Yalova bondex sünger; rebonded süngerpan yapısıyla darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olan, Yalova içi projelere özel ses yalıtım çözümüdür.',
  imagePath: '/images/bondex-3.webp',
  sku: 'BDX-YLV',
  mpn: 'BDX-YLV',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Yalova bondex sünger hangi projelerde tercih edilir?',
      answer:
        'Konut, villa, yazlık, apart otel, ofis, teknik oda ve makine hacimlerinde tercih edilir. Yalova’da özellikle yazlık ve karma kullanım projelerinde yoğun talep görür.',
    },
    {
      question: 'Yalova içinde teslimat ne kadar sürer?',
      answer:
        'Stok durumuna göre Yalova merkez ve çevre ilçelerde teslimatlar çoğunlukla 1-3 iş günü içinde planlanır. Büyük projelerde etaplı sevkiyat yapılabilir.',
    },
    {
      question: 'Hangi kalınlık daha uygun olur?',
      answer:
        'Hafif ihtiyaçlarda 10-20mm, orta-yüksek şikayetlerde 30-40mm, teknik ve zorlu senaryolarda 50mm ile bariyerli kombinasyonlar önerilir.',
    },
    {
      question: "Yalova'da teknik keşif desteği var mı?",
      answer:
        'Evet. Projenin kullanım tipi, metrajı ve hedef konforuna göre keşif, katman önerisi ve uygulama planı oluşturulur.',
    },
    {
      question: 'Yalova bondex sünger fiyatı ne kadar?',
      answer:
        "Yalova bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Net fiyat; kalınlık, metraj ve uygulama detayına göre değişir.",
    },
  ],
  reviews: [
    {
      authorName: 'Ahmet Yılmaz',
      datePublished: '2026-04-06',
      ratingValue: 5,
      title: 'Çınarcık yazlıkta çok fark etti',
      body:
        "Yazlık dairemizde zemin uygulaması yaptık. Üst komşu kaynaklı adım sesi ciddi şekilde azaldı.",
      verified: true,
    },
    {
      authorName: 'Zeynep Kaya',
      datePublished: '2026-04-12',
      ratingValue: 5,
      title: 'Merkez ofiste memnun kaldık',
      body:
        "Toplantı odası geçişlerinde ses taşmasını azaltmak için kullandık. Çalışma düzeni belirgin biçimde iyileşti.",
      verified: true,
    },
    {
      authorName: 'Emre Demir',
      datePublished: '2026-04-18',
      ratingValue: 4,
      title: 'Altınova teknik hacimde işe yaradı',
      body:
        'Makine odası kaynaklı gürültü için uyguladık. Ses etkisi önceye göre daha yönetilebilir seviyeye indi.',
      verified: true,
    },
  ],
};

export function CityBondexYalova() {
  const url = toAbsoluteUrl('/yalova-bondex-sunger');
  const title = 'Yalova Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Yalova bondex sünger, konut ve teknik projelerde darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olur. Echopan kalitesiyle sunulur.';

  const relatedSlugs = ['bondex-sunger', 'bondex-sunger-20mm', 'bondex-sunger-40mm', 'bariyerli-bondex-sunger'];
  const relatedBondex = relatedSlugs.map((slug) => products.find((p) => p.slug === slug)).filter((p): p is Product => Boolean(p));

  const waUrl = buildWhatsAppUrl('Merhaba, Yalova bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (yalova-bondex-sunger)');
  const displayPrice = yalovaProduct.priceTRY
    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(yalovaProduct.priceTRY)
    : null;

  const pageReviews =
    yalovaProduct.reviews?.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    })) || [];
  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((sum, r) => sum + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'Yalova içi düzenli stok' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kalınlık', v: '10mm-50mm' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Hizmet', v: 'Yalova merkez + ilçeler' },
    { k: 'Plan', v: 'Proje bazlı teknik kurgu' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Yalova Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${toAbsoluteUrl('/')}#website`,
        name: 'Bondex Sünger',
        url: toAbsoluteUrl('/'),
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${toAbsoluteUrl('/')}#organization`,
        name: 'bariyerlibondexsunger.com',
        url: toAbsoluteUrl('/'),
        logo: { '@type': 'ImageObject', url: toAbsoluteUrl('/images/bondex-logo.webp'), width: 512, height: 512 },
        contactPoint: [
          { '@type': 'ContactPoint', telephone: '+90 216 519 34 11', contactType: 'customer service', areaServed: 'TR', availableLanguage: ['tr'] },
          { '@type': 'ContactPoint', telephone: '+90 546 919 11 34', contactType: 'sales', areaServed: 'TR', availableLanguage: ['tr'] },
        ],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61575206163572',
          'https://x.com/KabinPods',
          'https://www.instagram.com/kabinpods/',
          'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
          'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
        ],
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        inLanguage: 'tr-TR',
        datePublished: '2026-04-27',
        dateModified: new Date().toISOString().split('T')[0],
        primaryImageOfPage: { '@type': 'ImageObject', url: toAbsoluteUrl(yalovaProduct.imagePath), width: 1200, height: 750 },
        about: [
          { '@type': 'Thing', name: 'Yalova Bondex Sünger' },
          { '@type': 'Thing', name: 'Yalova ses yalıtımı' },
          { '@type': 'Thing', name: 'yazlık konut ses konforu' },
        ],
        speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.ProductSEO', '.ProductPage h1'] },
        mainEntity: { '@id': `${url}#product` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumb.map((b, idx) => ({ '@type': 'ListItem', position: idx + 1, name: b.name, item: b.item })),
      },
      {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: yalovaProduct.name,
        image: [{ '@type': 'ImageObject', url: toAbsoluteUrl(yalovaProduct.imagePath), width: 1200, height: 750 }],
        description: yalovaProduct.description,
        brand: { '@type': 'Brand', name: 'Bondex' },
        category: yalovaProduct.category,
        url,
        sku: yalovaProduct.sku,
        mpn: yalovaProduct.mpn,
        productID: yalovaProduct.sku || yalovaProduct.mpn,
        material: 'Rebonded Süngerpan',
        audienceType: 'Business',
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Kategori', value: yalovaProduct.category },
          { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
          { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'Yalova' },
        ],
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'TRY',
          price: String(yalovaProduct.priceTRY),
          validFrom: validFromStr,
          priceValidUntil: priceValidUntilStr,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
          unitText: 'm²',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: String(yalovaProduct.priceTRY), priceCurrency: 'TRY', unitCode: 'MTK', unitText: 'm²' },
          hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
          shippingDetails: { '@id': `${url}#shippingDetails` },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avgRating.toFixed(1),
          reviewCount: pageReviews.length,
          bestRating: '5',
          worstRating: '1',
        },
        review: yalovaProduct.reviews?.map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.authorName },
          datePublished: r.datePublished,
          reviewBody: r.body,
          headline: r.title,
          ...(r.verified && { reviewAspect: 'Verification' }),
          reviewRating: { '@type': 'Rating', ratingValue: String(r.ratingValue), bestRating: '5', worstRating: '1' },
        })),
      },
      {
        '@type': 'MerchantReturnPolicy',
        '@id': `${url}#merchantReturnPolicy`,
        applicableCountry: { '@type': 'Country', name: 'TR' },
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      {
        '@type': 'OfferShippingDetails',
        '@id': `${url}#shippingDetails`,
        shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: { '@type': 'Country', name: 'TR' } },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
          businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
          cutoffTime: '14:00',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: yalovaProduct.faqs?.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
      },
    ],
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Yalova'da Bondex Sünger Uygulamalarında Doğru Kurgu</h2>
            <p className={styles.seoText}>
              Yalova küçük ölçekli bir şehir gibi görünse de ses yalıtımı ihtiyacı açısından oldukça karma bir profile sahiptir. Bir yanda
              merkezde apartman ve ofis yoğunluğu, diğer yanda Çınarcık-Armutlu hattında yazlık kullanım, Altınova tarafında ise teknik ve
              üretim odaklı hacimler görülür. Bu çeşitlilik nedeniyle tek bir senaryoyu merkeze alan çözümler çoğu projede yetersiz kalır.
            </p>
            <p className={styles.seoText}>
              Yalova bondex sünger yaklaşımı, problemin kaynağını doğru sınıflandırmaya dayanır: darbe sesi, konuşma geçişi, titreşim kaynaklı
              uğultu veya bunların kombinasyonu. Rebonded süngerpan yapısı bu noktada darbe ve titreşim aktarımını azaltmaya yardımcı olur.
              Ancak kalıcı sonuç, uygulama disiplinine bağlıdır. Yüzey hazırlığı, birleşimlerin kapanması, kenar sürekliliği ve katman planı
              birlikte yürütülmelidir.
            </p>
            <h3 className={styles.seoSubtitle}>Yazlık ve Sezonluk Kullanımın Etkisi</h3>
            <p className={styles.seoText}>
              Yalova'nın en belirgin farklarından biri sezonluk kullanımın yüksek olmasıdır. Yaz döneminde artan kullanım yoğunluğu, ses
              şikayetlerinin daha görünür hale gelmesine neden olur. Bu nedenle projelerde hız baskısı oluşabilir. Fakat hızlı çözüm ile
              doğru çözüm aynı şey değildir. Etaplı planlama yapılmadan tamamlanan uygulamalar kısa vadede etkili görünse de uzun vadede
              tekrar şikayet doğurabilir.
            </p>
            <p className={styles.seoText}>
              Yazlık projelerde kullanıcı beklentisi çoğunlukla gece konforu ve komşu etkisinin düşürülmesidir. Bu hedefe ulaşmak için tüm
              dairede aynı katmanı yaymak yerine hassas hacimlerde güçlendirilmiş çözüm modeli daha verimli olur.
            </p>
            <h3 className={styles.seoSubtitle}>Konut ve Ofis Senaryolarında Öncelikler</h3>
            <p className={styles.seoText}>
              Konutlarda üst kat adım sesi ve duvar geçişleri öne çıkarken, ofis projelerinde toplantı odası mahremiyeti ve çalışma alanı
              uğultusu ön plana çıkar. Bu iki ihtiyaç teknik olarak farklı ele alınmalıdır. Bu nedenle model seçerken{' '}
              <Link to="/bondex-sunger" rel="follow">bondex sünger</Link> ve gerektiğinde{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">bariyerli bondex</Link> alternatifleri birlikte değerlendirilir.
            </p>
            <h3 className={styles.seoSubtitle}>Kalınlık Seçimi ve Maliyet Dengesi</h3>
            <p className={styles.seoText}>
              20-30mm çoğu konut/ofis senaryosunda dengeli sonuç verirken, yüksek şikayetli teknik alanlarda 40-50mm çözümler gündeme gelir.
              En kalın ürünü seçmek her zaman en iyi çözüm değildir. Doğru yaklaşım, kritik alanları güçlendirip düşük riskli bölgelerde
              optimize katmanla ilerlemektir.
            </p>
            <h3 className={styles.seoSubtitle}>Yalova'da Problemi Doğru Tanımlama Adımları</h3>
            <p className={styles.seoText}>
              Yalova projelerinde en çok vakit kaybettiren konu, ses şikayetinin doğru sınıflandırılamamasıdır. Kullanıcı "ev çok ses alıyor"
              dediğinde teknik ekip bunun darbe sesi mi, konuşma geçişi mi, tesisat etkisi mi yoksa titreşim kaynaklı bir rezonans mı olduğunu
              netleştirmelidir. Bu ayrım yapılmadan seçilen sistem, kısa vadede kısmi rahatlama sağlasa da kalıcı başarı üretmeyebilir.
            </p>
            <p className={styles.seoText}>
              Doğru sınıflandırma için temel yöntem şu sırayla ilerler: sesin en çok hangi saatlerde arttığı, hangi mekanda daha fazla
              hissedildiği, tekil bir kaynak mı yoksa çoklu kaynak mı olduğu, kullanıcı hareketiyle tetiklenip tetiklenmediği gibi sorular
              cevaplanır. Bu sayede karar, duyuma göre değil veri odaklı şekilde verilir. Özellikle yazlık yapılarda dönemsel kullanım farkı
              olduğu için sezon öncesi ve sezon içi şikayet farklılaşabilir; bu da planı etkiler.
            </p>
            <h3 className={styles.seoSubtitle}>Yazlık Site ve Apartmanlarda Katman Stratejisi</h3>
            <p className={styles.seoText}>
              Çınarcık, Esenköy ve Armutlu hattında yazlık site projelerinde kullanıcıların en hassas olduğu konu gece konforudur. Özellikle
              üst kat adım sesi ve koridor kaynaklı geçişler rahatsızlık yaratır. Bu tip yapılarda tüm alana tek bir çözüm yaymak yerine
              yatak odası ve dinlenme hacimleri gibi kritik alanları önceliklendirmek daha verimli bir stratejidir.
            </p>
            <p className={styles.seoText}>
              Aynı zamanda yazlık yapılarda yıl boyunca sürekli kullanım olmadığı için bakım ve kontrol periyotları farklı planlanmalıdır.
              Uygulama sonrası kontrol yapılmazsa, küçük birleşim açıklıkları sezonda daha büyük soruna dönüşebilir. Bu nedenle proje teslimi
              sonrası kısa bir kontrol planı oluşturmak, performansın korunması açısından önemlidir.
            </p>
            <h3 className={styles.seoSubtitle}>Villa Projelerinde Sık Yapılan Hatalar</h3>
            <p className={styles.seoText}>
              Yalova'daki villa projelerinde en sık hata, sadece iç mekana odaklanıp teknik birimleri ikinci plana atmaktır. Oysa havuz
              ekipman odası, mekanik alanlar ve servis hacimleri doğru ele alınmazsa ses etkisi yaşam alanına dolaylı yoldan taşınır.
              Villa projelerinde çözüm, oda içinde iyi performans üretmek kadar altyapı kaynaklarını kontrol etmek üzerine kurulmalıdır.
            </p>
            <p className={styles.seoText}>
              İkinci yaygın hata, estetik kaygıyla tüm teknik detayları gizlemeye çalışmaktır. Gerektiği yerde bakım erişimi bırakılmadığında
              ileride oluşan teknik müdahaleler katmanı bozabilir. Dolayısıyla doğru mühendislik yaklaşımı, hem akustik verim hem bakım
              sürdürülebilirliği arasında denge kurmaktır.
            </p>
            <h3 className={styles.seoSubtitle}>Ofis ve Ticari Yapılarda Çalışma Konforu</h3>
            <p className={styles.seoText}>
              Yalova merkezdeki ofis projelerinde açık ofis düzeni yaygınlaştıkça yankı ve konuşma uğultusu problemleri artmaktadır. Bu
              problemi çözmenin yolu tüm hacmi tek katmanla kaplamak değil; toplantı odaları, yönetim alanları ve ortak çalışma bölgeleri
              için farklı hedef seviyeleri belirlemektir. Böylece her alan kendi işlevine uygun ses konforuna ulaşır.
            </p>
            <p className={styles.seoText}>
              Toplantı odası mahremiyetinde en kritik noktalar çoğu zaman duvarın ortası değil, kapı çevresi ve tavan birleşimleridir.
              Bu detaylar çözüldüğünde kullanıcı "odada konuşulan dışarı taşıyor" şikayetinin azaldığını daha hızlı fark eder. Aynı mantık,
              klinik, danışmanlık ofisi ve eğitim alanlarında da geçerlidir.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Hacimlerde Uygulama Mantığı</h3>
            <p className={styles.seoText}>
              Altınova ve çevresinde teknik hacim odaklı projelerde sorun çoğu zaman iki parçalıdır: birincisi hava kaynaklı ses, ikincisi
              yapıya aktarılan titreşim. Bu iki etkiyi tek katmanla çözmeye çalışmak yetersiz kalabilir. Kaynak noktaya yakın güçlendirme,
              taşıyıcı geçişlerini sınırlama ve birleşim sürekliliği bir arada ele alındığında daha stabil sonuç elde edilir.
            </p>
            <p className={styles.seoText}>
              Teknik projelerde bir diğer önemli başlık da işletme sürekliliğidir. Uygulama sonrasında bakım yolu kapanan bir sistem,
              işletmede yeni sorunlara yol açar. Bu nedenle katman çözümü geliştirilirken cihaz erişimi, servis kapağı ve güvenlik mesafeleri
              mutlaka korunmalıdır.
            </p>
            <h3 className={styles.seoSubtitle}>Uygulama Kalitesini Artıran Kontrol Adımları</h3>
            <p className={styles.seoText}>
              Sahadaki performansı belirleyen en önemli unsur kalite kontrol disiplinidir. Uygulama öncesinde yüzeyin kuru, temiz ve uygun
              taşıma kapasitesinde olduğunun teyit edilmesi gerekir. Uygulama sırasında ek yerleri ve köşe dönüşleri adım adım kontrol
              edilmeli; uygulama sonunda rastgele değil planlı bir son kontrol yapılmalıdır.
            </p>
            <p className={styles.seoText}>
              Bu yaklaşım tekrar iş oranını düşürür. Tekrar işin azalması, hem zaman kazancı sağlar hem de kullanıcıya daha güven veren
              bir süreç deneyimi oluşturur. Özellikle aynı yapıda birden fazla bağımsız bölüm varsa, standartlaştırılmış kontrol listesiyle
              ilerlemek işin kalitesini korur.
            </p>
            <h3 className={styles.seoSubtitle}>Lojistik ve Termin Yönetiminin Etkisi</h3>
            <p className={styles.seoText}>
              Yalova'da teslimat planı yapılırken mesafe kısa görünse bile şantiye erişimi, bina giriş koşulu, taşıma katları ve çalışma saat
              izinleri dikkate alınmalıdır. Özellikle yaz sezonunda yoğunluk artarken, plansız sevkiyat uygulama ekibinin beklemesine ve işin
              uzamasına neden olabilir. Bu yüzden büyük metrajlı işlerde etaplı sevkiyat modeli daha doğru sonuç verir.
            </p>
            <p className={styles.seoText}>
              Termin yönetimi yalnızca "başlangıç ve bitiş tarihi" vermek değildir. Hangi gün hangi alanın açılacağı, hangi katmanın ne zaman
              uygulanacağı, ara kontrolün ne zaman yapılacağı gibi detaylar plan içinde tanımlanmalıdır. Bu disiplin kurulduğunda proje daha
              öngörülebilir hale gelir ve kullanıcı tarafında güven artar.
            </p>
            <h3 className={styles.seoSubtitle}>Maliyet-Performans Planı Nasıl Kurulur?</h3>
            <p className={styles.seoText}>
              En pahalı çözüm her zaman en doğru çözüm değildir. Yalova projelerinde doğru yatırım, hedeflenen konfor seviyesini gereksiz
              metraj veya gereksiz kalınlık oluşturmadan yakalamaktır. Bunun için kritik alanlar güçlendirilir, düşük riskli alanlar optimize
              edilir. Bu yaklaşım toplam bütçeyi kontrol altında tutarken sonucu daha görünür hale getirir.
            </p>
            <p className={styles.seoText}>
              Kullanıcıya net teknik açıklama sunmak karar sürecini hızlandırır. Hangi kalınlığın neden seçildiği, hangi şikayeti hangi
              oranda hedeflediği şeffaf biçimde anlatıldığında proje yönetimi kolaylaşır. Bu şeffaflık uzun vadede marka güveni ve kullanıcı
              memnuniyeti açısından da güçlü katkı sağlar.
            </p>
            <h3 className={styles.seoSubtitle}>Yalova'ya Özel İçerik Neden Değerli?</h3>
            <p className={styles.seoText}>
              Yalova için hazırlanan içerik, şehir adını değiştirip aynı metni tekrarlayan bir model değildir. Yazlık kullanım baskısı, kıyı
              hattı yapıları, merkez ofis ihtiyaçları ve teknik hacim gerçekleri birlikte değerlendirilerek hazırlanmıştır. Bu yaklaşım
              kullanıcıya gerçek karşılığı olan bilgi sunar.
            </p>
            <p className={styles.seoText}>
              Eğer projeniz için hızlı ve net yönlendirme almak istiyorsanız kullanım tipi, metraj, şikayet tarifi ve hedef konfor seviyesini
              paylaşmanız yeterlidir. Bu bilgilerle birlikte uygun kalınlık ve katman modeli daha güvenli biçimde belirlenebilir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teknik Özellikler Özeti</h2>
            <p className={styles.seoText}>
              Yüksek yoğunluklu rebonded yapı, darbe ve titreşim etkisinin kontrolünde destek sağlar. Uygulama başarısı, birleşim kalitesi ve
              yüzey hazırlığı ile birlikte değerlendirilmelidir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Yalova'da Kullanım Alanları</h2>
            <p className={styles.seoText}>
              Konut, yazlık, ofis, teknik oda ve depo projelerinde kullanılır. Senaryoya göre zemin, duvar veya teknik hacim katmanında çözüm kurulur.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teslimat ve Süreç Yönetimi</h2>
            <p className={styles.seoText}>
              Yalova içi sevkiyat şantiye erişimine göre planlanır. Büyük metrajlı işlerde etaplı teslimat ve teknik kontrol adımlarıyla proje
              daha stabil yönetilir.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Yalova Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img className={styles.img} src={yalovaProduct.imagePath} alt="Yalova bondex sünger uygulaması" loading="eager" width="600" height="400" />
        </div>
        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Yalova Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-YLV | Yalova İçi Projeler
          </p>
          <p className={styles.meta}>
            Yalova'da <strong>bondex sünger</strong> ihtiyacı olan konut, yazlık, ofis ve teknik hacim projelerinde doğru kalınlık, hızlı
            tedarik ve proje bazlı teknik planlama sunuyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Yalova içi hızlı teslimat</span>
            <span className="chip">Yazlık ve konut uyumlu</span>
          </div>

          <div className={styles.specGrid} aria-label="Yalova için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {yalovaProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Yalova Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={yalovaProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
